
import { Controller } from '@hotwired/stimulus'
import { useMapReduce } from '../mixins/use_map_reduce'
import { useDirtyInputTracking, isDirty } from 'stimulus-library'
import { isElementCheckable, isHTMLSelectElement } from '@stimulus-library/utilities'

import {
  elementValue,
  isPresent,
  setElementEnabled
} from '../html_element_functions'

const requiredElementClass = 'required'
const validatableElementClass = 'validatable'
const unchangeableElementClass = 'unchangeable'
const CACHE_ATTR_NAME = 'data-detect-dirty-load-value'
const BUSY_SELECTOR = '[aria-busy="true"]'

/**
 * Form controller that supports required fields and validation. The form will not be submittable
 * until all required fields are populated, all validatable fields are valid, the form is
 * 'dirty' (at least one change is made to the form), and the form and its elements are not 'busy'.
 *
 * However, the form's dirty state can manually be overridden in two ways:
 *   1. Set the form's initializeDirty value
 *   2. Set the data-initialize-dirty attribute on any changeable form element
 * If either of these are set, the form will be considered dirty, even if no changes have been made.
 *
 * This controller supports the following HTML inputs:
 * - input (text, password, number, email, url, search, checkbox, etc.)
 * - textarea
 * - select
 *
 * This controller does not currently support required fieldsets that contain checkboxes. For example, requiring a user
 * to select at least one checkbox in a group of checkboxes.
 */
export default class FormController extends Controller {
  static values = {
    initializeDirty: {
      type: Boolean,
      default: false
    }
  }

  static get requiredElementClass() { return requiredElementClass }
  static get validatableElementClass() { return validatableElementClass }
  static get unchangeableElementClass() { return unchangeableElementClass }

  connect() {
    super.connect()
    this.#setRequiredFieldsPresent(false)
    this.#setInvalidFieldsPresent(true)
    this.#setChangedFieldsPresent(false)
    this.#initializeRequiredFieldsPipeline()
    this.#initializeValidatableFieldsPipeline()
    this.#initializeChangedFieldsPipeline()
    this.#registerSubmitButtonClickHandler()
  }

  updateCollection() {
    if(this.application.debug) {
      console.log('Updating form collection:', this.element)
    }
    this._requiredFieldsPipeline.updateCollection()
    this._validatableFieldsPipeline.updateCollection()
    this._changedFieldsPipeline.updateCollection()
  }

  refreshFormState() {
    if(this.application.debug) {
      console.log('Refreshing form state:', this.element)
    }
    this._requiredFieldsPipeline.execute()
    this._validatableFieldsPipeline.execute()
    this.#cacheChangeableFormElementValues()
    this._changedFieldsPipeline.execute()
  }

  enableSubmitButtons() {
    this.#submitButtons.forEach(button => setElementEnabled(button, this.#requiredFieldsPresent))
  }

  submitForm() {
    this.element.requestSubmit()
  }

  // Private controller methods

  get #form_busy() {
    return this.element.matches(BUSY_SELECTOR) || this.element.querySelector(BUSY_SELECTOR) !== null
  }

  #registerSubmitButtonClickHandler() {
    this.element.addEventListener('turbo:submit-end', event => {
      document.activeElement.blur()
      this.#submitButtons.forEach(button => button.setAttribute("disabled", true))
      if (event.detail.success) {
        this.initializeDirtyValue = false
        this.#cacheChangeableFormElementValues()
      }
      return true
    })
  }

  #initializeRequiredFieldsPipeline() {
    this._requiredFieldsPipeline = useMapReduce(this, {
      name: 'requiredFields',
      input: this.#requiredFields,
      map: elementValue,
      reduce: this.#requiredConditionsSatisfied,
      output: this.#setRequiredFieldsPresent
    })
  }

  #initializeValidatableFieldsPipeline() {
    this._validatableFieldsPipeline = useMapReduce(this, {
      name: 'validatableFields',
      input: this.#validatableFormInputs,
      map: this.#isInvalidFormInput,
      reduce: invalidFieldStatus => invalidFieldStatus.some(result => result),
      output: this.#setInvalidFieldsPresent
    })
  }

  #initializeChangedFieldsPipeline() {
    this._changedFieldsPipeline = useMapReduce(this, {
      name: 'changedFields',
      input: () => this.#changeableFormElements,
      events: ['input-dirtied', 'input-cleaned'],
      map: e => e,
      reduce: this.#detectChangedFields,
      output: this.#setChangedFieldsPresent
    })
  }

  /**
   * Cache the current value of changeable form elements so that we can detect changes.
   * Consider each element, by definition, not to be dirty (since cached value == current value).
   */
  #cacheChangeableFormElementValues(){
    this.#changeableFormElements.forEach((element) => {
      element.setAttribute(CACHE_ATTR_NAME, this.#getElementValueForCaching(element))
      delete element.dataset.dirty
    })
  }

  #setRequiredFieldsPresent(value) {
    this._requiredFieldsPresent = value
    this.#updateSubmitButtons()
  }

  get #requiredFieldsPresent() {
    return this._requiredFieldsPresent
  }

  #setInvalidFieldsPresent(value) {
    this._invalidFieldsPresent = value
    this.#updateSubmitButtons()
  }

  get #invalidFieldsPresent() {
    return this._invalidFieldsPresent
  }

  #setChangedFieldsPresent(value) {
    if (value === null) { return }

    this._changedFieldsPresent = this.initializeDirtyValue || value
    // Manually manage form data dirty state - ensure the data-dirty attribute on the form element
    // is accurate, as the dirty input tracking doesn't handle this automatically.
    this.element.dataset.dirty = this._changedFieldsPresent

    this.#updateSubmitButtons()
  }

  get #changedFieldsPresent() {
    return this._changedFieldsPresent
  }

  #updateSubmitButtons() {
    const enabled = this.#requiredFieldsPresent && !this.#invalidFieldsPresent && this.#changedFieldsPresent && !this.#form_busy
    this.#submitButtons.forEach(button => setElementEnabled(button, enabled))
  }

  get #submitButtons() {
    const items = this.element.elements.namedItem('submit')
    if (!items) throw new Error("Couldn't find form submit button")
    return items instanceof RadioNodeList ? Array.from(items) : [items]
  }

  #requiredFields() {
    const selector = `input.${requiredElementClass},  select.${requiredElementClass}, textarea.${requiredElementClass}`
    return this.#formElements.filter(element => element.matches(selector))
  }

  #requiredConditionsSatisfied(requiredFieldValues) {
    if (requiredFieldValues.length === 0) {
      return true
    }

    return requiredFieldValues.every(isPresent)
  }

  #detectChangedFields(elements) {
    return elements.some(element => isDirty(element))
  }

  #isInvalidFormInput(input) {
    return input.getAttribute('aria-invalid') === 'true'
  }

  #validatableFormInputs() {
    const selector = `input.${validatableElementClass},  select.${validatableElementClass}, textarea.${validatableElementClass}`
    return this.#formElements.filter(element => element.matches(selector))
  }

  get #formElements() {
    return Array.from(this.element.elements)
  }

  get #changeableFormElements() {
    return this.#formElements
               .filter(element => element.localName == 'input' || element.localName == 'select' || element.localName == 'textarea')
               .filter(element => !element.classList.contains(unchangeableElementClass))
               .filter(element => element.value !== undefined)
               .map(element => {
                 if (!element.hasAttribute(CACHE_ATTR_NAME)) {
                   this.#initializeChangeableFormElement(element)
                 }
                 return element
               })
  }

  #getElementValueForCaching(element) {
    if (isElementCheckable(element)) {
      return element.checked
    }
    else if (isHTMLSelectElement(element) && element.multiple) {
      return JSON.stringify(this.#getMultiSelectElementValuesForCaching(element))
    }
    else {
      return element.value
    }
  }

  #getMultiSelectElementValuesForCaching(element) {
    let options = Array.from(element.selectedOptions)
    return options.map(option => option.value)
  }

  #initializeChangeableFormElement(element) {
    // If any fields are initialized as dirty, we interpret that as the whole form being initialized dirty.
    if (element.dataset.initializeDirty === 'true') {
      this.initializeDirtyValue = true
    }

    useDirtyInputTracking(this, element)
  }
}
